import type { OrgType } from './persona'

export type SeasonRosterCategories = {
  darkGeese: number
  ducks: number
  snowRoss: number
  cranes: number
  coots: number
}

export type SeasonRosterTermDate = {
  termLabel: string
  start: string
  end: string
}

export type SeasonRosterEntry = {
  clientName: string
  address: string | null
  halId: string | null
  termDates: SeasonRosterTermDate[]
  huntDates: string
  huntingUnit: string | null
  categories: SeasonRosterCategories
}

export type RosterWarning = {
  type: 'missing_hal' | 'missing_address'
  clientName: string
  message: string
}

export type SeasonRosterResponse = {
  orgName: string
  season: string
  generatedAt: string
  clients: SeasonRosterEntry[]
  warnings: RosterWarning[]
}

export type SkCategory =
  | 'Dark Geese'
  | 'Ducks'
  | 'Snow/Ross'
  | 'Cranes'
  | 'Coots'
  | 'Other'

export type HarvestDetailSpecies = {
  speciesName: string
  count: number
}

export type HarvestDetailParticipant = {
  userId: string
  displayName: string
  halId: string | null
  species: HarvestDetailSpecies[]
  categoryTotals: Record<SkCategory, number>
}

export type HarvestDetailHunt = {
  huntId: string
  huntDate: string
  zoneName: string | null
  huntingUnit: string | null
  amLocation: string | null
  pmLocation: string | null
  participants: HarvestDetailParticipant[]
}

export type HarvestDetailResponse = {
  dateRange: { start: string; end: string }
  hunts: HarvestDetailHunt[]
}

export type IlDnr13Column =
  | 'canadaGoose'
  | 'snowBlueGoose'
  | 'specklebelly'
  | 'mallard'
  | 'blackDuck'
  | 'woodDuck'
  | 'gadwall'
  | 'wigeon'
  | 'greenWingedTeal'
  | 'blueWingedTeal'
  | 'shoveler'
  | 'pintail'
  | 'redhead'
  | 'canvasback'
  | 'lesserScaup'
  | 'ringNeckedDuck'
  | 'bufflehead'
  | 'coot'
  | 'unspecifiedDucks'

export const IL_DNR13_DISPLAY_ORDER: IlDnr13Column[] = [
  'canadaGoose',
  'snowBlueGoose',
  'specklebelly',
  'mallard',
  'blackDuck',
  'woodDuck',
  'gadwall',
  'wigeon',
  'greenWingedTeal',
  'blueWingedTeal',
  'shoveler',
  'pintail',
  'redhead',
  'canvasback',
  'lesserScaup',
  'ringNeckedDuck',
  'bufflehead',
  'coot',
  'unspecifiedDucks',
]

export const IL_DNR13_COLUMN_LABELS: Record<IlDnr13Column, string> = {
  canadaGoose: 'Canada Goose',
  snowBlueGoose: 'Snow/Blue Goose',
  specklebelly: 'Specklebelly (White-fronted Goose)',
  mallard: 'Mallard',
  blackDuck: 'Black Duck',
  woodDuck: 'Wood Duck',
  gadwall: 'Gadwall',
  wigeon: 'Wigeon (Baldpate)',
  greenWingedTeal: 'Green-winged Teal',
  blueWingedTeal: 'Blue-winged Teal',
  shoveler: 'Shoveler',
  pintail: 'Pintail',
  redhead: 'Redhead',
  canvasback: 'Canvasback',
  lesserScaup: 'Lesser Scaup (Bluebill)',
  ringNeckedDuck: 'Ring-necked Duck',
  bufflehead: 'Bufflehead',
  coot: 'Coot',
  unspecifiedDucks: 'Unspecified Ducks',
}

export type IlRegisterEntry = {
  date: string
  hunterName: string
  hunterAddress: string | null
  customerIdNumber: string | null
  species: Record<IlDnr13Column, number>
}

export type IlOutfitterRegisterResponse = {
  outfitterName: string | null
  county: string | null
  licenseNumber: string | null
  contactPerson: string | null
  huntingYear: string
  entries: IlRegisterEntry[]
}

export type GenericReportHunt = {
  huntId: string
  huntDate: string
  propertyName: string | null
  location: string | null
  timePeriod: string
  hunterNames: string[]
  hunterCount: number
  speciesCounts: Record<string, number>
  totalBirds: number
}

export type GenericReportTotals = {
  hunters: number
  speciesCounts: Record<string, number>
  birds: number
  birdsPerHunterPerHunt: number | null
}

export type GenericReportResponse = {
  dateRange: { start: string; end: string }
  species: string[]
  hunts: GenericReportHunt[]
  totals: GenericReportTotals
}

export type SavedReportType =
  | 'season-roster'
  | 'harvest-detail'
  | 'il-outfitter-register'
  | 'generic'

export type SavedReportParams = {
  start: string
  end: string
  county?: string
  licenseNumber?: string
}

export type SavedReportSnapshot =
  | SeasonRosterResponse
  | HarvestDetailResponse
  | IlOutfitterRegisterResponse
  | GenericReportResponse

export type SavedReportSummary = {
  id: string
  orgId: string
  orgType: OrgType
  reportType: SavedReportType
  title: string
  params: SavedReportParams
  rowCount: number
  createdBy: string
  createdAt: string
}

export type SavedReportDetail = SavedReportSummary & {
  snapshot: SavedReportSnapshot
}
